import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDebouncedCallback } from '@mantine/hooks';
import { errorModal } from 'vl-common/src/components/Modals';
import * as actions from '@actions';
import useDebounce from 'vl-common/src/hooks/useDebounce';
import { Call, Device, TwilioError } from '@twilio/voice-sdk';
import { Appointment } from 'vl-common/src/schemas/shared/appointment';
import { useDispatch } from 'react-redux';
import { useDPACheck } from '@hooks/useDPACheck';
import { useTranslation } from 'react-i18next';
import { useAudioCallStatus } from '@hooks/useAudioCallStatus';
import { useRinger } from '@hooks/useRinger';
import useRuntimeConfig from '@vl-core/hooks/useConfig';
import useSendTwilioEvents from '@hooks/useSendTwilioEvents';
import { useHeidiSession } from '@components/Clinicians/Session/hooks/useHeidiSession';
import { audioTranscriptionService } from '@lib/audioTranscriptionService';

export const CallStatusTextEnum = {
  CALLING: 'Calling Patient...',
  CONNECTED: 'Call Connected...',
  UNANSWERED: 'Patient did not answer',
  DECLINED: 'Patient declined call',
  UNKNOWN_END: 'No answer',
  UNAVAILABLE: 'Number unavailable',
  ENDED: 'Call ended...'
};

export const TWILIO_ERRORS = {
  CONNECTION_ERROR: 31402,
  DECLINE_ERROR: 31603
};

const CALL_SID_SETTLE_MS = 2000;
const AUDIO_ERROR_DEBOUNCE_MS = 2000;

const createAudioDevice = async (JWT: string, rtcConfiguration: any, deviceRef: React.RefObject<Device | null>) => {
  const newDevice = new Device(JWT, {
    enableImprovedSignalingErrorPrecision: true,
    // @ts-expect-error: rtcConfiguration is supported at runtime
    rtcConfiguration
  });

  deviceRef.current = newDevice;

  const newCleanup = () => {
    if (deviceRef.current) {
      deviceRef.current.disconnectAll();
      deviceRef.current.destroy();
      deviceRef.current = null;
      sessionStorage.removeItem('inCall');
    }
  };

  return { newDevice, newCleanup };
};

export default function useVoiceCalling(
  appointment: Appointment,
  stopVideoRinger: () => void,
  onAppointmentChange: () => void
) {
  const { t } = useTranslation('common');

  const [audioCallUI, setAudioCallUI] = useState(false);
  const [callName, setCallName] = useState('');
  const [callNumber, setCallNumber] = useState('');
  const [typeOfCall, setTypeOfCall] = useState(appointment.call_type_code);
  const dispatch = useDispatch();
  const [audioCallError, setAudioCallError] = useState<TwilioError.TwilioError>();
  const hideUIAfterPatientDisconnect = useRef<NodeJS.Timeout>(undefined);
  const { makeDPACheckRequired } = useDPACheck();
  const [currentInput, setCurrentInput] = useState('');
  const [currentOutput, setCurrentOutput] = useState('');
  const [callSid, setCallSid] = useState('');
  const [deviceRebuildCount, setDeviceRebuildCount] = useState(0);
  const { audioCallStatus, audioCallStatusText } = useAudioCallStatus(appointment, callSid);
  const sendTwilioEvents = useSendTwilioEvents();
  useRinger(audioCallStatus, appointment);
  const { AUDIO_SETTINGS } = useRuntimeConfig();
  const { pause, resume } = useHeidiSession();
  const isHangingUp = useRef<boolean>(false);
  const deviceRef = useRef<Device | null>(null);
  const activeCallRef = useRef<Call | null>(null);
  const [isAudioCallStarted, setIsAudioCallStarted] = useState(false);

  const { clinician_user_guid, appointment_id, patient_user_guid } = appointment;

  const displayAudioCallError = useCallback(async () => {
    if (audioCallError) {
      let displayError = audioCallError;
      let modalTitle = t('Failed to start call ({{code}})', { code: displayError.code });

      if (audioCallError.code === TWILIO_ERRORS.DECLINE_ERROR) {
        modalTitle = t('Call Declined');
        displayError = {
          ...audioCallError,
          message: t('The patient declined the call.')
        };
      }

      if (audioCallError.code === TWILIO_ERRORS.CONNECTION_ERROR) {
        const devices = await navigator.mediaDevices.enumerateDevices();
        if (!devices.some((d) => d.kind === 'audioinput')) {
          displayError = {
            ...audioCallError,
            message: t(
              'We are unable to detect a working microphone. A microphone is required to connect to this appointment.'
            ),
            solutions: [
              t('Check your hardware: Ensure your external microphone or headset is securely plugged in.'),
              t(
                'Check your Bluetooth: If you are using wireless headphones, verify they are currently connected to your computer.'
              ),
              t(
                "Check system settings: Open your computer's sound settings to ensure a microphone is recognized and enabled."
              )
            ]
          };
        }
      }

      errorModal({
        title: modalTitle,
        content: (
          <div>
            <p>{displayError.message}</p>
            {displayError.solutions?.length > 0 && (
              <>
                {t('Possible Solutions:')}
                <br />
                <ul>
                  {displayError.solutions?.map((err) => (
                    <li key={err}>{err}</li>
                  ))}
                </ul>
              </>
            )}
          </div>
        ),
        onOk: () => {
          setAudioCallError(undefined);
          return Promise.resolve();
        }
      });
      sendTwilioEvents({
        userGuid: clinician_user_guid,
        appointmentId: appointment_id,
        event: 'AUDIO_ERROR',
        message: JSON.stringify(displayError)
      }).then();
    }
  }, [audioCallError, clinician_user_guid, appointment_id, sendTwilioEvents, t]);

  useDebounce(displayAudioCallError, 500, [audioCallError]);

  useEffect(() => {
    let cleanup = () => {};

    const initialiseAudioDeviceLogging = (device: Device) => {
      device.on(Device.EventName.Registered, async () => {
        await sendTwilioEvents({
          userGuid: clinician_user_guid,
          appointmentId: appointment_id,
          event: 'AUDIO_READY',
          message: 'Twilio.Device is now ready for connections'
        });
      });

      device.on(Device.EventName.Unregistered, async () => {
        await sendTwilioEvents({
          userGuid: clinician_user_guid,
          appointmentId: appointment_id,
          event: 'AUDIO_OFFLINE',
          message: 'Twilio.Device is offline'
        });
      });
    };

    const setupAudioDevice = async () => {
      const tokenResponse = await dispatch(actions.getVoiceCallToken());
      const JWT = tokenResponse.payload;
      if (!JWT) return;

      const { newDevice, newCleanup } = await createAudioDevice(JWT, AUDIO_SETTINGS?.rtcConfiguration, deviceRef);

      initialiseAudioDeviceLogging(newDevice);

      deviceRef.current = newDevice;
      cleanup = newCleanup;
      setCurrentInput(newDevice.audio?.inputDevice?.deviceId || 'default');
      const speakerDevices = newDevice.audio?.speakerDevices?.get() || [];
      setCurrentOutput([...speakerDevices]?.[0]?.deviceId || 'default');
    };

    setupAudioDevice().then();

    // whenever the device changes, or we unmount, destroy the *previous* device
    return () => {
      audioTranscriptionService.setVoiceCall(null);
      activeCallRef.current = null;
      cleanup();
    };
  }, [
    dispatch,
    AUDIO_SETTINGS?.rtcConfiguration,
    clinician_user_guid,
    appointment_id,
    deviceRebuildCount,
    sendTwilioEvents
  ]);

  const hangupVoiceCall = useCallback(async () => {
    // ensure disconnect from call as device rebuild is unreliable if sendTwilioEvents fails
    setIsAudioCallStarted(false);
    if (activeCallRef.current) {
      activeCallRef.current.disconnect();
      activeCallRef.current = null;
    }
    if (isHangingUp.current) return;
    isHangingUp.current = true;
    try {
      audioTranscriptionService.setVoiceCall(null);
      setDeviceRebuildCount((prev) => prev + 1);
      await sendTwilioEvents({
        userGuid: patient_user_guid,
        appointmentId: appointment_id,
        event: 'CAUDIOEND'
      });
    } catch (e) {
      console.error(e);
    } finally {
      isHangingUp.current = false;
    }
    onAppointmentChange();
  }, [patient_user_guid, appointment_id, sendTwilioEvents, onAppointmentChange]);

  const onClickHangupCall = useCallback(() => {
    if (hideUIAfterPatientDisconnect.current) {
      clearTimeout(hideUIAfterPatientDisconnect.current);
      hideUIAfterPatientDisconnect.current = undefined;
    }
    setAudioCallUI(false);
    hangupVoiceCall().then();
  }, [hangupVoiceCall]);

  useEffect(() => {
    if (!callSid || !deviceRef.current || !audioCallUI) return;

    const callEndedStatuses = ['completed', 'busy', 'failed', 'no-answer', 'canceled'];

    if (callEndedStatuses.includes(audioCallStatus)) {
      pause();
      hideUIAfterPatientDisconnect.current = setTimeout(() => {
        setAudioCallUI(false);
      }, 4000);
    }
    return () => {
      if (hideUIAfterPatientDisconnect.current) {
        clearTimeout(hideUIAfterPatientDisconnect.current);
        pause();
      }
    };
  }, [callSid, audioCallStatus, audioCallUI, pause]);

  const handleAudioError = useCallback(
    async (error: TwilioError.TwilioError) => {
      setIsAudioCallStarted(false);
      pause();
      console.error('error occurred:', error);
      setAudioCallError(error);
      sendTwilioEvents({
        userGuid: clinician_user_guid,
        appointmentId: appointment_id,
        event: 'CLINICIAN_CALL_FAILED',
        message: JSON.stringify(error)
      }).then();
      try {
        audioTranscriptionService.setVoiceCall(null);
        activeCallRef.current = null;
        setDeviceRebuildCount((prev) => prev + 1);
        setAudioCallUI(false);
        if (hideUIAfterPatientDisconnect.current) {
          clearTimeout(hideUIAfterPatientDisconnect.current);
          hideUIAfterPatientDisconnect.current = undefined;
        }
      } catch (e) {
        console.error(e);
      }
    },
    [clinician_user_guid, appointment_id, sendTwilioEvents, pause]
  );

  const debouncedHandleAudioError = useDebouncedCallback(handleAudioError, {
    delay: AUDIO_ERROR_DEBOUNCE_MS,
    flushOnUnmount: true
  });

  const sendStartAudioEvent = useCallback(
    async (audioConnection: Call, appointment: Appointment) => {
      // wait for Twilio to populate call.parameters.CallSid, otherwise CAUDIOSTART is sent without it
      await new Promise((resolve) => setTimeout(resolve, CALL_SID_SETTLE_MS));

      setCallSid(audioConnection?.parameters?.CallSid);

      const audioCallData = {
        CallSid: audioConnection?.parameters?.CallSid,
        number: audioConnection?.customParameters?.get('number')
      };
      await sendTwilioEvents({
        userGuid: appointment.patient_user_guid,
        appointmentId: appointment.appointment_id,
        event: 'CAUDIOSTART',
        message: JSON.stringify(audioCallData)
      });
    },
    [sendTwilioEvents]
  );

  const onCallPatient = useCallback(
    async (appointment: Appointment, callPrimary: boolean) => {
      setCallSid('');
      let patientNumber = callPrimary ? appointment.patient_tel_mobile_no : appointment.patient_tel_other_no;
      const patientName = appointment.patient;
      const audioDevice = deviceRef.current;
      if (!patientNumber || !audioDevice) return;

      stopVideoRinger();
      if (hideUIAfterPatientDisconnect.current) {
        clearTimeout(hideUIAfterPatientDisconnect.current);
        hideUIAfterPatientDisconnect.current = undefined;
      }
      setCallName(patientName);
      setCallNumber(patientNumber);
      if (patientNumber.charAt(3) === '0') {
        patientNumber = patientNumber.slice(0, 3) + patientNumber.slice(4);
      }

      audioDevice.off('error', debouncedHandleAudioError);
      audioDevice.on('error', debouncedHandleAudioError);
      return audioDevice
        .connect({
          params: {
            number: patientNumber,
            appointment_id: appointment.appointment_id.toString(),
            user_guid: appointment.clinician_user_guid
          }
        })
        .then(async (call) => {
          activeCallRef.current = call;
          // not awaited so the call window appears straight away rather than 2s later
          sendStartAudioEvent(call, appointment).catch((e) => console.error(e));
          setAudioCallUI(true);

          call.on('disconnect', () => {
            audioTranscriptionService.setVoiceCall(null);
            pause();
            hangupVoiceCall();
          });

          call.on('error', debouncedHandleAudioError);

          call.on('accept', async (audioConnection: Call) => {
            setIsAudioCallStarted(true);
            activeCallRef.current = audioConnection;
            audioTranscriptionService.setVoiceCall(audioConnection);
            resume();
            await dispatch(actions.sendAudioCallStarted(appointment.appointment_id, patientNumber));
            // Show security when audio call has taken place
            makeDPACheckRequired();
            sessionStorage.setItem('inCall', 'true');
            setTypeOfCall('AUDIO');
            audioConnection.on('disconnect', () => {
              setIsAudioCallStarted(false);
              audioTranscriptionService.setVoiceCall(null);
              activeCallRef.current = null;
              sessionStorage.removeItem('inCall');
            });
          });
        })
        .catch((error) => {
          audioTranscriptionService.setVoiceCall(null);
          activeCallRef.current = null;
          errorModal({
            title: t('An error occurred'),
            content: t(
              'There was an error starting your call, please try again. If you still experience issues, try refreshing the page.'
            )
          });
          throw error;
        });
    },
    [
      dispatch,
      stopVideoRinger,
      makeDPACheckRequired,
      sendStartAudioEvent,
      debouncedHandleAudioError,
      hangupVoiceCall,
      pause,
      resume,
      t
    ]
  );

  const updateInputDevice = useCallback(async (deviceId: string) => {
    await deviceRef.current?.audio?.setInputDevice(deviceId);
    setCurrentInput(deviceId);
  }, []);

  const updateOutputDevice = useCallback(async (deviceId: string) => {
    await deviceRef.current?.audio?.speakerDevices.set(deviceId);
    await deviceRef.current?.audio?.ringtoneDevices.set(deviceId);
    setCurrentOutput(deviceId);
  }, []);

  /** Memoized attributes to be passed to <CallPatient /> component */
  const callPatientProps = useMemo(
    () => ({ name: callName, number: callNumber, audioCallStatusText, onClickHangupCall }),
    [callName, callNumber, audioCallStatusText, onClickHangupCall]
  );

  return {
    onCallPatient,
    callPatientProps,
    onClickHangupCall,
    audioCallStatus,
    audioCallStatusText,
    typeOfCall,
    audioCallUI,
    isAudioCallStarted,
    updateInputDevice,
    currentInput,
    updateOutputDevice,
    currentOutput
  };
}
